import React, { useEffect, useMemo, useState } from 'react';
import topo from '../assets/counties.topo.json';
import { LICENSED_STATE_CODES } from '../constants/marketExpansion';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// THE COVERAGE MAP. A RENDERER OVER THE RULE — it defines nothing.
//
// 🔑 SHADING COMES FROM GET /api/service-area/counties, which derives from the
// same three tables the ZIP check reads. The map cannot disagree with what a
// borrower is told, because it is not a second source.
//
// ⚠️ THE ZIP SEARCH IS AUTHORITATIVE WHERE THEY DIFFER, AND THE MAP SAYS SO ON
// ITS FACE. A county map cannot express a ZIP that straddles a rural and a
// non-rural county — nine of the 437 ZIPs in the retired service area do
// exactly that, and the rule is "eligible if ANY county is non-rural". Those
// ZIPs sit inside a county the map shades ineligible and are eligible anyway.
//
// ⚠️ FIPS ARE OBJECT KEYS, NEVER AN ARRAY. A run of five-digit string literals
// is indistinguishable from a ZIP list to backend/test/noGeographicLiterals —
// `['48001','48005',…]` trips its ZIP_RUN pattern. Keyed lookup avoids the
// collision by construction rather than by an allow-list entry.
//
// ⚠️ AND COUNTY NAMES COME FROM THE SERVER. "Colorado County" is a real Texas
// county and is on the CFPB rural list; as a literal here it would trip the
// guard's two-state prose rule, since Colorado and Texas are both state names.
// No county name is written in frontend/src.
//
// ⚠️ NO MAPPING LIBRARY. The geometry is one state of quantised TopoJSON built
// at build time (scripts/buildCountyGeometry.js, 56 KB); decoding it and fitting
// a viewBox is less code than d3-geo would add bundle.
// ─────────────────────────────────────────────────────────────────────────────

/** Quantised TopoJSON arc → absolute coordinates. */
function decodeArc(arc, transform) {
  const [sx, sy] = transform.scale;
  const [tx, ty] = transform.translate;
  let x = 0, y = 0;
  return arc.map(([dx, dy]) => { x += dx; y += dy; return [x * sx + tx, y * sy + ty]; });
}

function buildPaths(topology) {
  const arcs = topology.arcs.map((a) => decodeArc(a, topology.transform));
  const ring = (idx) => idx.reduce((pts, i) => {
    const a = i < 0 ? arcs[~i].slice().reverse() : arcs[i];
    return pts.length ? pts.concat(a.slice(1)) : a.slice();
  }, []);
  const polys = (g) => (g.type === 'Polygon' ? [g.arcs] : g.arcs);
  return topology.objects.counties.geometries.map((g) => ({
    id: String(g.id),
    rings: polys(g).flatMap((p) => p.map(ring)),
  }));
}

// ⚠️ THE DEFAULT STATE IS DERIVED, NOT 'TX'. The guard caught this as a bare
// geographic literal and it was right to: a hardcoded default in a coverage map
// is exactly the thing that goes stale when the licence list moves. It reads the
// mirror, which backend/test/licenseClaims compares against the backend.
export default function CoverageMap({ state, token, detail = false, highlight = null }) {
  const shownState = state || LICENSED_STATE_CODES[0];
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/service-area/counties?state=${shownState}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : r.json().then((b) => Promise.reject(new Error(b.error || `HTTP ${r.status}`)))))
      .then((b) => alive && setData(b))
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [shownState, token]);

  const shapes = useMemo(() => buildPaths(topo), []);

  // 🔑 KEYED, NOT ARRAYED. See the header.
  const byFips = useMemo(() => {
    const m = {};
    for (const c of (data?.counties || [])) m[c.fips] = c;
    return m;
  }, [data]);

  // ── 🚨 THE PROJECTION. TEXAS WAS STRETCHED 53% WIDE ─────────────────────
  //
  // Two mistakes compounding. Longitude and latitude are NOT the same distance:
  // at Texas's mean latitude of 31.2°N a degree of longitude covers cos(31.2°) =
  // 0.856 of a degree of latitude. And the viewBox was a fixed 1000×620, so
  // whatever shape came out was squashed into 1.613:1 regardless.
  //
  // Texas spans 13.13° of longitude and 10.66° of latitude — a raw ratio of
  // 1.232, but a TRUE ratio of (13.13 × 0.856) / 10.66 = 1.054. It is very
  // nearly square. Rendering it at 1.613 made it 53% too wide.
  //
  // 🔑 THE FIX IS A STANDARD PARALLEL, not a mapping library. Scale x by the
  // cosine of the mean latitude and let the viewBox follow the result. That is
  // an equirectangular projection with its standard parallel through the middle
  // of the state — correct enough for one state at this size, and d3-geo would
  // have been 30 KB to do the same arithmetic.
  //
  // ⚠️ IT IS DERIVED FROM THE GEOMETRY, not a constant for Texas. A second
  // licensed state gets its own mean latitude and its own aspect with no edit.
  const box = useMemo(() => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const s of shapes) for (const r of s.rings) for (const [x, y] of r) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    const k = Math.cos(((y0 + y1) / 2) * (Math.PI / 180));
    const w = (x1 - x0) * k;
    const h = y1 - y0;
    const W = 1000;
    return { x0, y0, k, w, h, W, H: Math.round((W * h) / w) };
  }, [shapes]);

  // ── 🔑 THE SEARCHED ZIP, MARKED ON THE COUNTIES IT FALLS IN ─────────────
  //
  // ⚠️ A ZIP IS NOT A SHAPE ON THIS MAP. The map is drawn by county, so the
  // honest highlight is the county or counties the ZIP sits in — and a quarter
  // of Texas ZIPs sit in more than one, which is why this is a SET and why the
  // caption says "counties" rather than naming one.
  //
  // ⚠️ IT OUTLINES, IT DOES NOT RECOLOUR. Filling the county with a "you are
  // here" colour would overwrite the eligible/not-eligible shading, which is
  // the one thing the map exists to say. An outline adds without replacing.
  const marked = useMemo(() => {
    const set = new Set();
    for (const c of (highlight?.counties || [])) if (c.fips) set.add(c.fips);
    return set;
  }, [highlight]);

  if (err) return <div style={{ fontSize: 13, color: '#b91c1c' }}>Coverage map unavailable: {err}</div>;
  if (!data) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading coverage…</div>;

  // Latitude flips: SVG y grows downward. x is scaled by the standard parallel
  // BEFORE normalising, which is what corrects the stretch.
  const px = ([x, y]) => `${(((x - box.x0) * box.k) / box.w) * box.W},${((box.y0 + box.h - y) / box.h) * box.H}`;
  const d = (s) => s.rings.map((r) => `M${r.map(px).join('L')}Z`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${box.W} ${box.H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
        aria-label={`Coverage by county for ${data.state}`}>
        {shapes.map((s) => {
          const c = byFips[s.id];
          const on = marked.has(s.id);
          const fill = !c ? '#f3f4f6' : c.eligible ? '#d4f99a' : '#f3f4f6';
          return (
            <path key={s.id} d={d(s)} fill={fill}
              stroke={on ? '#3a6ad9' : '#fff'} strokeWidth={on ? 2.4 : 0.8}
              onMouseEnter={() => setHover(c || null)} onMouseLeave={() => setHover(null)} />
          );
        })}
        {/* ⚠️ THE OUTLINES ARE DRAWN TWICE, the second pass on top. SVG paints
            in document order, so a neighbouring county drawn later would paint
            over the highlight's stroke and leave it half-erased. */}
        {shapes.filter((s) => marked.has(s.id)).map((s) => (
          <path key={`hl-${s.id}`} d={d(s)} fill="none" stroke="#3a6ad9" strokeWidth="2.4"
            strokeLinejoin="round" pointerEvents="none" />
        ))}
      </svg>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 8, fontSize: 12, color: '#374151', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#d4f99a', border: '1px solid #b9e86a', borderRadius: 2, marginRight: 5, verticalAlign: '-1px' }} />We generally lend</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 2, marginRight: 5, verticalAlign: '-1px' }} />We do not</span>
        {hover && <span style={{ color: '#6b7280' }}>{hover.name} — {hover.eligible ? 'covered' : 'not covered'}{detail ? ` (${hover.basis})` : ''}</span>}
        {/* 🔑 NAMES WHAT THE OUTLINE MEANS. An unexplained ring on a map is a
            thing people invent a meaning for. County names come from the
            server — none is written in frontend/src. */}
        {!hover && marked.size > 0 && highlight?.counties && (
          <span style={{ color: '#3a6ad9', fontWeight: 600 }}>
            Outlined: {highlight.counties.map((c) => c.name).join(', ')}
            {highlight.zip ? ` — ZIP ${highlight.zip}` : ''}
          </span>
        )}
      </div>

      {/* 🚨 THE MAP SAYS WHICH YEAR'S RULE IT SHOWS, AND DEFERS TO THE SEARCH.
          A map that cannot name its own vintage is one somebody reads as current
          forever; and county shading cannot express a ZIP spanning two counties,
          which nine real ZIPs do. */}
      <p style={{ marginTop: 8, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.6 }}>
        Shaded by county, CFPB rural list {data.publication_year} ({data.citation}).
        A ZIP can span more than one county — <strong>the ZIP search above is
        authoritative</strong> where the two differ.
      </p>
    </div>
  );
}
